import { ApiError, apiRequest } from "./apiClient";
import type { GameRuleGeneratedDocumentRow } from "../types/gameRuleGeneratedDocument";

const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

type KbGeneratedDocumentListApiItem = {
  readonly id: string;
  readonly name: string;
  readonly file_name: string;
  readonly generated_at: string;
  readonly section_count: number;
  readonly reviewer_email?: string | null;
  readonly status: string;
};

type KbGeneratedDocumentListResponse = {
  readonly success?: boolean;
  readonly data: unknown[];
  readonly meta: { readonly total: number; readonly page: number; readonly limit: number };
};

type KbGeneratedDocumentDownloadResponse = {
  readonly success?: boolean;
  readonly data?: {
    readonly document_web_url?: string | null;
    readonly document_download_url?: string | null;
    readonly file_name?: string;
  };
};

type KbGeneratedDocumentDetailResponse = {
  readonly success?: boolean;
  readonly data?: {
    readonly stored_file_id?: string;
  };
};

function resolvedFileName(row: GameRuleGeneratedDocumentRow, fromApi?: string | null): string {
  const fromMeta = fromApi?.trim() || row.fileName?.trim();
  if (fromMeta) return fromMeta;
  const safeName = row.name.replace(/[/\\?%*:|"<>]/g, "_").trim() || "game-rule";
  return safeName.toLowerCase().endsWith(".docx") ? safeName : `${safeName}.docx`;
}

async function fetchStoredFileBase64(storedFileId: string): Promise<string> {
  const response = await apiRequest(
    `/stored-files/${encodeURIComponent(storedFileId)}/content-base64`,
    undefined,
  );
  const json = (await response.json()) as { data?: { base64?: string } };
  const b64 = json.data?.base64;
  if (typeof b64 !== "string" || b64.length === 0) {
    throw new ApiError("Download response missing file data", 502);
  }
  return b64;
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}

function triggerBlobFileDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

function triggerUrlDownload(url: string, fileName: string): void {
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

async function fetchStoredFileId(documentId: string): Promise<string> {
  const response = await apiRequest(
    `/game-rule/generated-documents/${encodeURIComponent(documentId)}`,
    undefined,
  );
  const json = (await response.json()) as KbGeneratedDocumentDetailResponse;
  const storedFileId = json.data?.stored_file_id;
  if (!storedFileId) {
    throw new ApiError("Document has no stored file.", 404);
  }
  return String(storedFileId);
}

/**
 * Download the committed DOCX via KB download endpoint; falls back to stored-file bytes
 * when SharePoint/Graph URLs are absent (local file storage).
 */
export async function downloadGeneratedGameRuleDocument(row: GameRuleGeneratedDocumentRow): Promise<void> {
  const response = await apiRequest(
    `/game-rule/generated-documents/${encodeURIComponent(row.id)}/download`,
    undefined,
  );
  const json = (await response.json()) as KbGeneratedDocumentDownloadResponse;
  const data = json.data;
  const fileName = resolvedFileName(row, data?.file_name);
  const direct =
    data?.document_download_url?.trim() || data?.document_web_url?.trim() || null;
  if (direct) {
    triggerUrlDownload(direct, fileName);
    return;
  }
  const storedFileId = await fetchStoredFileId(row.id);
  const b64 = await fetchStoredFileBase64(storedFileId);
  const blob = base64ToBlob(b64, DOCX_MIME);
  triggerBlobFileDownload(blob, fileName);
}

function mapListRow(row: KbGeneratedDocumentListApiItem): GameRuleGeneratedDocumentRow {
  return {
    id: String(row.id),
    name: row.name,
    fileName: row.file_name,
    generatedAt: row.generated_at,
    sectionCount: row.section_count ?? 0,
    reviewerEmail: row.reviewer_email ?? null,
    status: row.status,
  };
}

function parseListRow(raw: Record<string, unknown>): GameRuleGeneratedDocumentRow | null {
  if (
    typeof raw.id !== "string" ||
    typeof raw.name !== "string" ||
    typeof raw.file_name !== "string" ||
    typeof raw.generated_at !== "string" ||
    typeof raw.status !== "string"
  ) {
    return null;
  }
  return mapListRow({
    id: raw.id,
    name: raw.name,
    file_name: raw.file_name,
    generated_at: raw.generated_at,
    section_count: typeof raw.section_count === "number" ? raw.section_count : 0,
    reviewer_email: typeof raw.reviewer_email === "string" ? raw.reviewer_email : null,
    status: raw.status,
  });
}

export type ListGameRuleGeneratedDocumentsParams = {
  readonly page: number;
  readonly limit: number;
  readonly status?: string;
  readonly search?: string;
};

export async function listGameRuleGeneratedDocumentsPaginated(
  params: ListGameRuleGeneratedDocumentsParams,
): Promise<{ readonly items: GameRuleGeneratedDocumentRow[]; readonly total: number }> {
  const sp = new URLSearchParams();
  sp.set("page", String(params.page));
  sp.set("limit", String(params.limit));
  sp.set("status", params.status?.trim() || "all");
  if (params.search?.trim()) {
    sp.set("search", params.search.trim());
  }
  const response = await apiRequest(`/game-rule/generated-documents?${sp.toString()}`, undefined);
  const json = (await response.json()) as KbGeneratedDocumentListResponse;
  const rawList = Array.isArray(json.data) ? json.data : [];
  const items: GameRuleGeneratedDocumentRow[] = [];
  for (const row of rawList) {
    if (row && typeof row === "object") {
      const parsed = parseListRow(row as Record<string, unknown>);
      if (parsed) items.push(parsed);
    }
  }
  const total = typeof json.meta?.total === "number" ? json.meta.total : items.length;
  return { items, total };
}
